/**
 * Service to manage reservation trend data consistently across the dashboard
 */

// Cache key for localStorage
const TREND_DATA_KEY = 'reservationTrendData';

// Days of the week for consistent naming
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Get stored trend data from localStorage
 * @returns {Array} Trend data array or empty array if none found
 */
const getStoredTrendData = () => {
  try {
    const storedData = localStorage.getItem(TREND_DATA_KEY);
    if (storedData) {
      const parsed = JSON.parse(storedData);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.map(entry => {
        const migrated = { ...entry };
        if (typeof migrated.new === 'number') {
          migrated.reserved = (migrated.reserved || 0) + migrated.new;
          delete migrated.new;
        }
        if (typeof migrated.confirmed === 'number') {
          migrated.booked = (migrated.booked || 0) + migrated.confirmed;
          delete migrated.confirmed;
        }
        if (typeof migrated.cancelled === 'number') {
          migrated.canceled = (migrated.canceled || 0) + migrated.cancelled;
          delete migrated.cancelled;
        }
        return migrated;
      });
    }
  } catch (error) {
    console.error("Error retrieving stored trend data:", error);
  }
  return [];
};

/**
 * Save trend data to localStorage
 * @param {Array} data Trend data to store
 */
const saveTrendData = (data) => {
  try {
    localStorage.setItem(TREND_DATA_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error storing trend data:", error);
  }
};

/**
 * Get the display key for a date based on period
 * @param {Date} date Date to get key for
 * @param {string} period Period type ('thisWeek', 'lastWeek', 'last30Days')
 * @returns {string} Display key for the date
 */
const getDisplayKey = (date, period = 'thisWeek') => {
  if (period === 'thisWeek' || period === 'lastWeek') {
    return DAYS[date.getDay()];
  } else if (period === 'last30Days') {
    return `${date.getDate()} ${MONTHS[date.getMonth()]}`;
  } else {
    return date.toISOString().split('T')[0];
  }
};

/**
 * Update trend data with a new status change
 * @param {Date} date Date of the status change
 * @param {string} status Status to increment ('reserved', 'booked', 'completed', 'canceled')
 * @param {string} period Period type, defaults to 'thisWeek'
 */
const updateTrendData = (date, status, period = 'thisWeek') => {
  const normalizeStatus = (value) => {
    const statusMap = {
      new: 'reserved',
      confirmed: 'booked',
      cancelled: 'canceled',
      canceled: 'canceled',
      reserved: 'reserved',
      booked: 'booked',
      completed: 'completed'
    };
    return statusMap[value] || null;
  };

  const normalizedStatus = normalizeStatus(status);

  // Only handle valid statuses
  if (!normalizedStatus) {
    console.warn(`Invalid status: ${status}`);
    return;
  }

  // Get current trend data
  let trendData = getStoredTrendData();
  
  // If no data, initialize with default structure
  if (!trendData || trendData.length === 0) {
    trendData = initializeDefaultTrendData(period);
  }

  // Get the appropriate display key for the date
  const displayKey = getDisplayKey(date, period);
  
  // Find the entry to update
  const entryIndex = trendData.findIndex(entry => entry.name === displayKey);
  
  if (entryIndex >= 0) {
    // Update existing entry
    trendData[entryIndex][normalizedStatus] = (trendData[entryIndex][normalizedStatus] || 0) + 1;
  } else {
    // Create new entry if not found
    const newEntry = {
      name: displayKey,
      reserved: 0,
      booked: 0,
      completed: 0,
      canceled: 0
    };
    newEntry[normalizedStatus] = 1;
    trendData.push(newEntry);
    
    // Sort by day of week for thisWeek and lastWeek
    if (period === 'thisWeek' || period === 'lastWeek') {
      trendData.sort((a, b) => {
        const dayIndexA = DAYS.indexOf(a.name);
        const dayIndexB = DAYS.indexOf(b.name);
        return dayIndexA - dayIndexB;
      });
    }
  }
  
  // Save updated data
  saveTrendData(trendData);
  
  return trendData;
};

/**
 * Initialize trend data with default values for the given period
 * @param {string} period Period type
 * @returns {Array} Default trend data
 */
const initializeDefaultTrendData = (period = 'thisWeek') => {
  if (period === 'thisWeek' || period === 'lastWeek') {
    // Create entries for all days of the week
    return DAYS.map(day => ({
      name: day,
      reserved: 0,
      booked: 0,
      completed: 0,
      canceled: 0
    }));
  } else if (period === 'last30Days') {
    // For 30 days, create entries with current date
    const today = new Date();
    const result = [];
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      
      const day = date.getDate();
      const month = MONTHS[date.getMonth()];
      
      result.push({
        name: `${day} ${month}`,
        reserved: 0,
        booked: 0,
        completed: 0,
        canceled: 0
      });
    }
    
    return result;
  }
  
  // Default empty array
  return [];
};

/**
 * Get trend data for a specific period 
 * @param {string} period Period type
 * @returns {Array} Trend data for the period
 */
const getTrendData = (period = 'thisWeek') => {
  let trendData = getStoredTrendData();
  
  // If no data or empty, initialize with defaults
  if (!trendData || trendData.length === 0) {
    trendData = initializeDefaultTrendData(period);
    saveTrendData(trendData);
  }
  
  return trendData;
};

/**
 * Reset trend data for a specific period
 * @param {string} period Period to reset
 */
const resetTrendData = (period = 'thisWeek') => {
  const newData = initializeDefaultTrendData(period);
  saveTrendData(newData);
  return newData;
};

// Fix ESLint warning by creating a named service object before exporting
const ReservationTrendService = {
  updateTrendData,
  getTrendData,
  resetTrendData,
  getDisplayKey
};

// Export the service
export default ReservationTrendService;
